// Admin Lessons Management Component
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X, Search, Sparkles } from 'lucide-react';
import { AIAssistant } from '../../components/AIAssistant';
import LessonContentEditor, { migrateToBlocks, makeRichTextBlock } from '../../components/LessonContentEditor';
import SortableList, { DragHandle } from '../../components/admin/SortableList';

import { getApiBaseUrl } from '@/lib/apiBase';
import { buildVideoSrc } from '@/lib/videoSrc';
const API_URL = getApiBaseUrl();

export const AdminLessons = () => {
  const { token } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderVersion, setOrderVersion] = useState(0);  // remounts the position inputs after each save
  const [formData, setFormData] = useState({
    course_id: '', title: '',
    content_blocks: [makeRichTextBlock()],
    videos: [{ type: 'youtube', url: '', title: 'Part 1' }],
    notes_url: '', duration_minutes: 15, is_free: false, order: 0, unlimited_views: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  const handleAIInsert = (content, type) => {
    if (type === 'text') {
      setFormData(prev => {
        const blocks = [...prev.content_blocks];
        const firstRich = blocks.findIndex(b => b.type === 'rich_text');
        if (firstRich >= 0) {
          blocks[firstRich] = { ...blocks[firstRich], content };
        } else {
          blocks.unshift({ ...makeRichTextBlock(), content });
        }
        return { ...prev, content_blocks: blocks };
      });
    }
    setAiAssistantOpen(false);
  };

  const fetchData = async () => {
    try {
      const [lessonsRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/lessons${selectedCourse ? `?course_id=${selectedCourse}` : ''}`, { headers }),
        axios.get(`${API_URL}/admin/courses`, { headers })
      ]);
      setLessons(Array.isArray(lessonsRes.data) ? lessonsRes.data : []);
      const coursesData = coursesRes.data?.courses || coursesRes.data || [];
      setCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, selectedCourse]);

  const handleVideoUpload = async (e, videoIndex) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const res = await axios.post(`${API_URL}/admin/upload/video`, uploadData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      // Backend returns { url, video_id, filename, size } — use 'url' (older
      // code was reading res.data.video_url which is undefined and caused
      // uploads to silently store an empty URL).
      const newVideos = [...formData.videos];
      newVideos[videoIndex] = {
        ...newVideos[videoIndex],
        url: res.data?.url || res.data?.video_url || '',
        type: 'local',
      };
      setFormData({ ...formData, videos: newVideos });
      toast.success('Video uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const addVideo = () => {
    const newVideos = [...formData.videos, { type: 'youtube', url: '', title: `Part ${formData.videos.length + 1}` }];
    setFormData({ ...formData, videos: newVideos });
  };

  const removeVideo = (index) => {
    if (formData.videos.length <= 1) {
      toast.error('Lesson must have at least one video');
      return;
    }
    const newVideos = formData.videos.filter((_, i) => i !== index);
    const updatedVideos = newVideos.map((v, i) => ({ ...v, title: `Part ${i + 1}` }));
    setFormData({ ...formData, videos: updatedVideos });
  };

  const updateVideo = (index, field, value) => {
    const newVideos = [...formData.videos];
    newVideos[index] = { ...newVideos[index], [field]: value };
    setFormData({ ...formData, videos: newVideos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Flatten the first video into top-level video_url / video_type / youtube_url
      // so that public-facing pages (LessonPlayer, mobile LessonScreen) which read
      // these flat fields can render the video. The full `videos` array is still
      // persisted for multi-part lesson support.
      const firstVideo = formData.videos?.[0] || { type: 'youtube', url: '' };
      const isYoutube = firstVideo.type === 'youtube';
      // Strip content_blocks from payload; store the blocks array as `description`
      // (backend accepts dict — stores it as-is, returns as-is).
      const { content_blocks, ...rest } = formData;
      const payload = {
        ...rest,
        description: content_blocks,
        video_type: firstVideo.type || 'youtube',
        video_url: isYoutube ? '' : firstVideo.url || '',
        youtube_url: isYoutube ? firstVideo.url || '' : '',
      };

      if (editingLesson) {
        await axios.put(`${API_URL}/admin/lessons/${editingLesson.id}`, payload, { headers });
        toast.success('Lesson updated');
      } else {
        await axios.post(`${API_URL}/admin/lessons`, payload, { headers });
        toast.success('Lesson created');
      }
      setDialogOpen(false);
      setEditingLesson(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error('Failed to save lesson');
    }
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    const videos = lesson.videos || (lesson.video_url || lesson.youtube_url ? [{
      type: lesson.video_type || 'youtube',
      url: lesson.video_url || lesson.youtube_url || '',
      title: 'Part 1'
    }] : [{ type: 'youtube', url: '', title: 'Part 1' }]);
    const content_blocks = migrateToBlocks(lesson.description);
    setFormData({ ...lesson, videos, content_blocks });
    setDialogOpen(true);
  };

  const handleDelete = async (lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;
    try {
      await axios.delete(`${API_URL}/admin/lessons/${lessonId}`, { headers });
      toast.success('Lesson deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete lesson');
    }
  };

  const resetForm = () => {
    setFormData({
      course_id: selectedCourse || '', title: '',
      content_blocks: [makeRichTextBlock()],
      videos: [{ type: 'youtube', url: '', title: 'Part 1' }],
      notes_url: '', duration_minutes: 15, is_free: false, order: 0, unlimited_views: false
    });
  };

  const filteredLessons = lessons.filter(lesson =>
    lesson.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lesson.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Lesson order is per course and a reorder sends the course's full lesson list,
  // so reordering needs a single course selected and no search filter.
  const canReorder = !!selectedCourse && !searchQuery && lessons.length > 1;

  const saveLessonOrder = async (request) => {
    const previous = lessons;
    setSavingOrder(true);
    try {
      const res = await request();
      const byId = Object.fromEntries(previous.map(l => [l.id, l]));
      setLessons(res.data.ordered_ids.map((id, i) => ({ ...byId[id], order: i + 1 })));
      toast.success('Lesson order saved');
    } catch (err) {
      setLessons(previous);
      toast.error(err?.response?.data?.detail || 'Failed to save lesson order');
    } finally {
      setSavingOrder(false);
      setOrderVersion(v => v + 1);
    }
  };

  const handleLessonReorder = (reordered) => {
    setLessons(reordered.map((l, i) => ({ ...l, order: i + 1 })));  // optimistic
    saveLessonOrder(() => axios.put(
      `${API_URL}/admin/courses/${selectedCourse}/lessons/reorder`,
      { ordered_ids: reordered.map(l => l.id) },
      { headers }
    ));
  };

  const handleMoveTo = (lesson, index, input) => {
    const newPos = parseInt(input.value, 10);
    if (isNaN(newPos) || newPos < 1 || newPos > lessons.length || newPos === index + 1) {
      input.value = index + 1;
      return;
    }
    saveLessonOrder(() => axios.put(
      `${API_URL}/admin/courses/${selectedCourse}/lessons/${lesson.id}/move`,
      { new_position: newPos },
      { headers }
    ));
  };

  const parentCourse = courses.find(c => c.id === formData.course_id);
  const courseIsPremium = parentCourse ? !parentCourse.is_free : false;

  return (
    <div data-testid="admin-lessons">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Lessons</h1>
          <p className="text-muted-foreground">{lessons.length} total lessons</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search lessons..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Select value={selectedCourse || 'all'} onValueChange={(v) => setSelectedCourse(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Courses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map((course) => (
                <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setAiAssistantOpen(true)} data-testid="ai-assistant-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Assistant
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingLesson(null); resetForm(); }} data-testid="create-lesson-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Lesson
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingLesson ? 'Edit Lesson' : 'Create Lesson'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Course *</Label>
                  <Select value={formData.course_id || 'select'} onValueChange={(v) => setFormData({...formData, course_id: v === 'select' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select" disabled>Select a course</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div>
                  <Label className="mb-2 block">Content Blocks</Label>
                  <LessonContentEditor
                    blocks={formData.content_blocks}
                    onChange={(blocks) => setFormData({ ...formData, content_blocks: blocks })}
                    token={token}
                  />
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Videos ({formData.videos?.length || 0})</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addVideo}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Video
                    </Button>
                  </div>
                  
                  {formData.videos?.map((video, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-sm">{video.title || `Part ${index + 1}`}</span>
                        {formData.videos.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeVideo(index)}>
                            <X className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <Label>Video Type</Label>
                          <Select value={video.type} onValueChange={(v) => updateVideo(index, 'type', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="youtube">YouTube Embed</SelectItem>
                              <SelectItem value="local">Upload Video</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {video.type === 'youtube' ? (
                          <div>
                            <Label>YouTube URL</Label>
                            <Input 
                              value={video.url} 
                              onChange={(e) => updateVideo(index, 'url', e.target.value)} 
                              placeholder="https://www.youtube.com/watch?v=..." 
                            />
                            {video.url && (
                              <div className="mt-2 aspect-video bg-black rounded-lg overflow-hidden max-w-2xl">
                                <iframe 
                                  src={video.url.replace('watch?v=', 'embed/')}
                                  className="w-full h-full"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <Label>Upload Video</Label>
                            <div className="flex items-center gap-4">
                              <Input type="file" accept="video/*" onChange={(e) => handleVideoUpload(e, index)} disabled={uploading} />
                              {uploading && <span className="text-sm text-muted-foreground">Uploading...</span>}
                            </div>
                            {video.url && (
                              <div className="mt-2">
                                <p className="text-sm text-green-600">Video uploaded</p>
                                <div className="mt-2 aspect-video bg-black rounded-lg overflow-hidden max-w-2xl">
                                  <video src={buildVideoSrc(video.url, token)} controls className="w-full h-full object-contain" />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Order</Label>
                    <Input type="number" min={1} placeholder="Last" value={formData.order || ''} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value, 10) || 0})} />
                    <p className="text-xs text-muted-foreground mt-1">Position in the course — other lessons shift automatically. Leave blank to add at the end.</p>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={courseIsPremium ? false : formData.is_free}
                      disabled={courseIsPremium}
                      onCheckedChange={(v) => !courseIsPremium && setFormData({...formData, is_free: v})}
                    />
                    <Label>Free Lesson</Label>
                  </div>
                  {courseIsPremium && (
                    <p className="text-xs text-muted-foreground mt-1">
                      🔒 Locked — this lesson is premium because its course is marked premium
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={!!formData.unlimited_views} onCheckedChange={(v) => setFormData({...formData, unlimited_views: v})} />
                  <Label>Unlimited Views (exempt from view limits)</Label>
                </div>
                <DialogFooter>
                  <Button type="submit">{editingLesson ? 'Update' : 'Create'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-muted-foreground mb-3" data-testid="lesson-reorder-hint">
            {canReorder
              ? (savingOrder
                  ? 'Saving order...'
                  : 'Drag the ⋮⋮ handle, or type a position number and press Enter, to reorder lessons. Numbers update automatically.')
              : selectedCourse
                ? 'Clear the search to reorder lessons.'
                : 'Select a course to reorder its lessons.'}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Order</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Course</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableList
                items={filteredLessons}
                onReorder={handleLessonReorder}
                disabled={!canReorder || savingOrder}
                renderItem={(lesson, index, { ref, style, handleProps }) => (
                <TableRow key={lesson.id} ref={ref} style={style} className="bg-background" data-testid={`lesson-row-${lesson.id}`}>
                  <TableCell>
                    {canReorder ? (
                      <div className="flex items-center gap-1">
                        <DragHandle handleProps={handleProps} disabled={savingOrder} />
                        <Input
                          key={`${lesson.id}-${orderVersion}`}
                          type="number"
                          min={1}
                          max={lessons.length}
                          defaultValue={index + 1}
                          disabled={savingOrder}
                          onBlur={(e) => handleMoveTo(lesson, index, e.target)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          className="h-8 w-16 text-center font-semibold"
                          title="Type a position and press Enter to move this lesson"
                          aria-label={`Position of ${lesson.title}`}
                        />
                      </div>
                    ) : lesson.order}
                  </TableCell>
                  <TableCell className="font-medium">{lesson.title}</TableCell>
                  <TableCell>{lesson.course_title || '-'}</TableCell>
                  <TableCell>{lesson.duration_minutes} min</TableCell>
                  <TableCell>
                    <Badge variant={lesson.is_free ? 'secondary' : 'default'}>
                      {lesson.is_free ? 'Free' : 'Premium'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(lesson)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(lesson.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                )}
              />
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <AIAssistant 
        isOpen={aiAssistantOpen} 
        onClose={() => setAiAssistantOpen(false)} 
        onInsert={handleAIInsert}
        token={token}
      />
    </div>
  );
};

export default AdminLessons;
